import { useState } from 'react';
import Modal from '@/shared/components/Modal';
import { S } from '@/shared/constants/styles';

function equalSplit(total, members) {
  if (!members.length) return [];
  const base = Math.floor((total * 100) / members.length) / 100;
  const splits = members.map(who => ({ who, amount: base }));
  // Give remainder cents to first person to avoid float drift
  const sumRest = base * members.length;
  splits[0].amount = Math.round((total - sumRest + base) * 100) / 100;
  return splits;
}

export default function SplitTransactionModal({ transaction, memberNames, onSplit, onClose, theme: t, isDark }) {
  const [splits, setSplits] = useState(() => equalSplit(transaction.amount, memberNames));
  const [errors, setErrors] = useState({});

  const total = transaction.amount;
  const splitSum = splits.reduce((s, sp) => s + (parseFloat(sp.amount) || 0), 0);
  const diff = Math.round((total - splitSum) * 100) / 100;
  const isValid = Math.abs(diff) < 0.01;

  const setAmount = (idx, val) => {
    setSplits(prev => prev.map((sp, i) => i === idx ? { ...sp, amount: val } : sp));
    setErrors(e => { const n = { ...e }; delete n.sum; return n; });
  };

  const handleSplit = () => {
    const parsed = splits.map(sp => ({ who: sp.who, amount: parseFloat(sp.amount) }));
    if (parsed.some(sp => sp.amount <= 0 || isNaN(sp.amount))) {
      setErrors({ sum: 'All amounts must be greater than 0' });
      return;
    }
    if (!isValid) {
      setErrors({ sum: `Amounts must add up to ${total.toFixed(2)}. Difference: ${Math.abs(diff).toFixed(2)}` });
      return;
    }
    onSplit(transaction.id, parsed);
    onClose();
  };

  const fmtAUD = (v) => (Number(v) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Modal onClose={onClose} title="Split Transaction" cardBg={t.cardBg} cardBorder={t.cardBorder} textColor={t.text}>
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: t.text, marginBottom: 3 }}>{transaction.description}</div>
        <div style={{ fontSize: 12, color: t.textMuted }}>
          Total: ${fmtAUD(total)} &bull; {transaction.category} &bull; {transaction.date}
        </div>
      </div>

      {errors.sum && (
        <div style={{
          background: t.errorBg, border: '1px solid ' + t.errorBorder,
          borderRadius: 8, padding: '8px 12px', fontSize: 12, color: t.errorText,
        }}>
          {errors.sum}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {splits.map((sp, idx) => (
          <div key={sp.who} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1, fontSize: 13, fontWeight: 500, color: t.text }}>{sp.who}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 13, color: t.textMuted }}>$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={sp.amount}
                onChange={e => setAmount(idx, e.target.value)}
                style={{ ...S.input, width: 100, fontSize: 13 }}
              />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: isValid ? '#16a34a' : '#ef4444', fontWeight: 600 }}>
          {isValid
            ? `Splits balance (total: $${fmtAUD(total)})`
            : `${diff > 0 ? `$${diff.toFixed(2)} unassigned` : `$${Math.abs(diff).toFixed(2)} over total`}`}
        </span>
        <button
          type="button"
          onClick={() => setSplits(equalSplit(total, memberNames))}
          style={{ ...S.link('#6366f1'), fontSize: 11 }}
        >
          Reset equal split
        </button>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onClose} style={{ ...S.btn('#64748b'), flex: 1 }}>Cancel</button>
        <button
          onClick={handleSplit}
          style={{ ...S.btn(), flex: 1, opacity: isValid ? 1 : 0.6 }}
        >
          Split Transaction
        </button>
      </div>
    </Modal>
  );
}
